/* eslint-disable @typescript-eslint/no-explicit-any */
/* eslint-disable react-hooks/exhaustive-deps */
import { Box, Tabs, Tab, Button, CircularProgress } from '@mui/material';
import { makeStyles } from 'tss-react/mui';
import { GenericDialog } from 'app/components/Alerts/GenericDialog';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { candidateDetailsSelection } from '../../Profile/CandidateDetails.selector';
import {
  addCandidateJobPreferences,
  editCandidateJobPreferences,
} from 'app/services/CandidateServices/CandidateServices';
import { globalActions } from 'app/ApplicationRoot/Global.redux';
import { jobPreferncesActions } from './store/JobPreferences.redux';
import { selectJobPreferencesData, selectJobPreferencesModalProps } from './store/JobPreferences.selectors';
import { GenericDialogSnackBar } from './JobPreferencesGrid/PreferenceDeletePopup/GenericDialogSnackBar';
import WarningIcon from '@mui/icons-material/Warning';
import { selectUser } from 'oidc/user.selectors';
import { userRoles } from 'oidc/userRoles';
import { Authorized } from 'oidc/userHelper';
import OrderPreferencesForm from './OrderPreferencesForm';
import { FormProvider, useForm } from 'react-hook-form';
import { JobMatchFormFields, tabs } from './helper';

const useStyles = makeStyles<{ expanded: boolean }>()((theme, expanded) => ({
  action: {
    display: 'flex',
    flexDirection: 'row',
  },
  actionBtn: {
    fontSize: 12,
    padding: 0,
    minWidth: 30,
  },
  actionNoBtn: {
    color: '#888888',
  },

  dialog: {
    minWidth: '960px',
    '& .MuiDialog-paperWidthSm': {
      minWidth: '960px',
      maxWidth: '100%',
    },
  },
  content: {
    height: '453px',
    overflowX: 'hidden',
  },
  indicator: {
    width: '5px',
    left: '0px',
    background: '#52429A',
  },
  filterTabs: {
    background: theme.palette.framework.system.whisper,
    height: '453px',
    width: '225px',
  },
  tab: {
    marginLeft: '10px',
    textTransform: 'none',
    color: '#555555',
    alignItems: 'flex-start !important',
    justifyContent: 'flex-start',
    '& .MuiTab-wrapper': {
      alignItems: 'flex-start !important',
      justifyContent: 'flex-start',
    },
    '&.Mui-selected': {
      color: '#52429A',
    },
  },

  container: {
    height: expanded ? '100%' : '453px',
    display: 'flex',
  },
  validationLoader: {
    position: 'absolute',
    left: 'calc(50% - 20px)',
    top: '50%',
  },
  form: {
    padding: 8,
  },
  dialogueContent: {
    padding: 0,
    '& .MuiDialogContent-root': {
      padding: 0,
    },
    '& .dialogContentRoot': {
      padding: 0,
    },
  },
  fullScreenContainer: {
    height: '100%',
    overflow: 'hidden',
    overflowY: 'scroll',
  },
  filterTabsExpanded: {
    height: '100vh',
    background: theme.palette.framework.system.whisper,
    width: '225px',
  },
}));

function a11yProps(index) {
  return {
    id: `full-width-tab-${index}`,
    'aria-controls': `full-width-tabpanel-${index}`,
  };
}
interface IJobPreferenceModalProps {
  open: boolean | false;
  title: string | '';
  onClose: (event: unknown, reason?: string) => void;
}

const JobPreferencesModal = (props: IJobPreferenceModalProps) => {
  const { jobPreferencesData } = useSelector(selectJobPreferencesData);
  const { id } = useSelector(selectJobPreferencesModalProps);
  const user = useSelector(selectUser);
  const count = jobPreferencesData?.length >= 0 && jobPreferencesData?.length + 1;
  const isoff = jobPreferencesData.filter(item => item?.id === id)?.[0]?.isOff;
  const defaultValues = {
    preferenceSetName: `Pref. Set ${count || ''}`,
    discipline: undefined,
    speciality: undefined,
    state: undefined,
    city: undefined,
    zip: undefined,
    radius: undefined,
    day: undefined,
    evening: undefined,
    night: undefined,
    notes: '',
    orderStartDateRange: undefined,
    placementLength: [0, 26],
    skillSetsArray: [],
    locations: [],
    skillSets: [],
    education: undefined,
    shifts: [],
  };
  const methods = useForm({
    defaultValues: defaultValues,
  });
  const {
    control,
    handleSubmit,
    formState: { errors },
    watch,
    setValue,
    getValues,
    reset,
  } = methods;
  //Values & States
  const { open, title, onClose } = props;
  const { t } = useTranslation();
  const [expanded, setExpanded] = React.useState<boolean>(false);
  const { classes } = useStyles({ expanded: expanded });
  const [selectedTab, setSelectedTab] = React.useState<any>(0);
  const [disabled, setDisabled] = React.useState<boolean>(false);
  const candidate = useSelector(candidateDetailsSelection);
  const dispatch = useDispatch();

  const [isSaving, setIsSaving] = React.useState<boolean>(false);
  const preferencenameField = watch(JobMatchFormFields.preferenceSetName);
  const snackbarID = new Date().getTime() + Math.random();

  //Helper-Functions

  const handleChange = (event, newValue) => {
    const ElementId = tabs[newValue].id;
    const element = document.getElementById(ElementId);
    element.scrollIntoView({ behavior: 'smooth' });
    setSelectedTab(newValue);
  };

  const showCloseSnackBar = () => {
    dispatch(
      globalActions.setSnackBar({
        id: snackbarID,
        message: 'Are you sure you want to exit without saving changes?',
        severity: 'warning',
        actions: [
          {
            action: () => handleClose(),
            closeSnackbar: true,
            text: t('defaultPage.yes'),
          },
          {
            closeSnackbar: true,
            text: t('defaultPage.no'),
          },
        ],
      }),
    );
  };

  const handleCloseModal = reason => {
    if (reason !== 'backdropClick') {
      handleClose();
    }
  };
  const handleClose = () => {
    onClose('');
    dispatch(jobPreferncesActions.setModalProps(undefined));
    setDisabled(false);
    setSelectedTab(0);
  };

  React.useEffect(() => {
    setDisabled(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const savePreferences = async () => {
    setIsSaving(true);
    let response;
    const values = getValues();
    const shift = [];
    values.day && shift.push(0);
    values.evening && shift.push(1);
    values.night && shift.push(2);
    const shiftIds = values.shifts && values.shifts.map(item => item.value);

    const payload = {
      ...values,
      shift: shift,
      candidateId: `${candidate.travelerId}`,
      brandId: `${candidate.brandId}`,
      recruiterId: `${candidate.recruiter.recruiterId}`,
      education: (values.education?.length > 0 && values.education.map(item => item.value)) || [],
      shiftIds: shiftIds || [],
      candidateName: `${candidate.name.first} ${candidate.name.last}`,
    };
    try {
      if (id) {
        response = await editCandidateJobPreferences({ ...payload, id, isOff: isoff });
        if (response.status === 200) {
          dispatch(
            globalActions.setSnackBar({
              message: t('candidate.jobPreferences.updateOrderPreference'),
              severity: 'success',
            }),
          );
          dispatch(
            jobPreferncesActions.getJobPreferencesAction({
              travelerId: candidate.travelerId,
              brandId: candidate.brandId,
            }),
          );
          handleClose();
        } else {
          dispatch(
            globalActions.setSnackBar({
              message: `${response?.data?.type}`,
              severity: 'error',
            }),
          );
        }
      } else {
        response = await addCandidateJobPreferences(payload);
        if (response.status === 200) {
          dispatch(
            globalActions.setSnackBar({
              message: t('candidate.jobPreferences.addOrderPreference'),
              severity: 'success',
            }),
          );
          dispatch(
            jobPreferncesActions.getJobPreferencesAction({
              travelerId: candidate.travelerId,
              brandId: candidate.brandId,
            }),
          );
          handleClose();
        } else {
          const message = response?.data?.title ?? response?.data.message;
          dispatch(
            globalActions.setSnackBar({
              message: `${message}`,
              severity: 'error',
            }),
          );
        }
      }
    } catch (error) {
      dispatch(
        globalActions.setSnackBar({
          message: `${error.message}`,
          severity: 'error',
        }),
      );
    }
    setIsSaving(false);
  };

  const handleExpanded = () => {
    setExpanded(!expanded);
  };

  return (
    <div>
      <GenericDialog
        open={open}
        onClose={(e, reason) => (disabled ? showCloseSnackBar() : handleCloseModal(reason))}
        variant="blue"
        className={classes.dialog}
        dialogTitleProps={{
          text: title,
          closeButton: true,
          expandable: true,
          onExpandChange: handleExpanded,
        }}
        dialogActions={[
          {
            text: 'Cancel',
            variant: 'contained',
            color: 'tertiary',
            onClick: () => (disabled ? showCloseSnackBar() : handleClose()),
          },
          {
            text: 'Save',
            variant: 'contained',
            color: 'primary',
            disabled: !disabled,
            onClick: savePreferences,
            tooltipProps: {
              tooltipContent: 'Preference Set name is required',
              disabled: disabled,
            },
          },
        ]}
        dialogContentProps={{
          classes: {
            root: classes.dialogueContent,
          },
        }}
      >
        <div className={classes.container}>
          {isSaving && <CircularProgress className={classes.validationLoader} />}
          <Box className={expanded ? classes.filterTabs : classes.filterTabsExpanded}>
            <Box style={{ marginTop: '20px' }}>
              <Tabs
                value={selectedTab}
                classes={{
                  indicator: classes.indicator,
                }}
                textColor="primary"
                orientation="vertical"
                onChange={handleChange}
                aria-label="edit product selector"
              >
                {tabs.map((tab, index) => (
                  <Tab
                    disabled={isSaving}
                    label={t(`${tab.label}`)}
                    className={classes.tab}
                    id={tab.id}
                    {...a11yProps(index)}
                    key={index}
                  />
                ))}
              </Tabs>
            </Box>
          </Box>
          <div className={expanded ? classes.content : classes.fullScreenContainer}>
            <div className={classes.form}>
              <FormProvider {...methods}>
                <OrderPreferencesForm
                  expanded={expanded}
                  selectedTab={selectedTab}
                  setDisabled={setDisabled}
                  isSaving={isSaving}
                />
              </FormProvider>
            </div>
          </div>
        </div>
      </GenericDialog>
    </div>
  );
};

export default JobPreferencesModal;
